import { z } from "zod";
import { ImagingFacilityFormReferenceSchema } from "./orders.js";
import { IdempotencyKeySchema, IdSchema, Sha256Schema } from "./primitives.js";
export const DocumentDiagnosticServiceBindingSchema = z
    .object({
    serviceName: z.string().trim().min(1).max(240),
    serviceType: z.literal("ambulatory_ecg"),
    durationMinutes: z.number().int().positive().max(43_200),
    origin: z.enum(["mapper_proposed", "human_confirmed"]),
    code: z.string().trim().min(1).max(80).optional(),
    codeSystem: z.literal("local").optional(),
})
    .strict()
    .superRefine((binding, context) => {
    if ((binding.code === undefined) !== (binding.codeSystem === undefined))
        context.addIssue({
            code: "custom",
            path: ["code"],
            message: "A diagnostic service code and code system must be supplied together",
        });
});
export const ClinicDiagnosticServiceConceptSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    codeSystem: z.literal("local"),
    display: z.string().trim().min(1).max(240),
    serviceType: z.literal("ambulatory_ecg"),
    durationMinutes: z.number().int().positive().max(43_200),
    sourceDocumentId: IdSchema,
    sourceDocumentVersion: z.number().int().positive(),
    sourceDocumentVersionHash: Sha256Schema,
})
    .strict();
export const ClinicDiagnosticServiceConceptListSchema = z
    .object({ services: z.array(ClinicDiagnosticServiceConceptSchema).max(5_000) })
    .strict();
export const ClinicDiagnosticDestinationSchema = z
    .object({
    id: IdSchema,
    name: z.string().trim().min(1).max(160),
    availabilityVersion: z.number().int().nonnegative(),
})
    .strict();
export const ClinicDiagnosticDestinationListSchema = z
    .object({ destinations: z.array(ClinicDiagnosticDestinationSchema).max(100) })
    .strict();
export const ClinicDiagnosticServiceCodeSchema = z
    .object({ code: z.string().trim().min(1).max(80), codeSystem: z.literal("local") })
    .strict();
export const AssociateClinicDiagnosticServicesCommandSchema = z
    .object({
    siteId: IdSchema,
    destinationId: IdSchema,
    expectedAvailabilityVersion: z.number().int().nonnegative(),
    documentId: IdSchema,
    documentVersion: z.number().int().positive(),
    documentVersionHash: Sha256Schema,
    services: z.array(ClinicDiagnosticServiceCodeSchema).min(1).max(500),
    verified: z.literal(true),
    verificationNote: z.string().trim().min(10).max(2_000),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const ClinicDiagnosticServiceAssociationReceiptSchema = z
    .object({
    destinationId: IdSchema,
    availabilityVersion: z.number().int().positive(),
    addedCodes: z.array(ClinicDiagnosticServiceCodeSchema).max(500),
    form: ImagingFacilityFormReferenceSchema,
    externalTransmissionEnabled: z.literal(false),
})
    .strict();
/** Registry metadata alone never authorizes automated Holter completion. */
export function isManualDiagnosticServiceField(field) {
    return (field.source.kind === "structured_order" &&
        field.source.path === "structured_order.diagnostic_service" &&
        field.disposition === "manual" &&
        field.diagnosticServiceBinding?.origin === "human_confirmed" &&
        field.reviewStatus === "reviewed" &&
        (field.fieldType === "checkbox" || field.fieldType === "radio") &&
        !field.authoritative &&
        !field.readOnlyAtRuntime);
}
//# sourceMappingURL=diagnostic-services.js.map